import React, { useState, useEffect } from "react";
import FormField from "@cloudscape-design/components/form-field";
import Multiselect from "@cloudscape-design/components/multiselect";
import Input from "@cloudscape-design/components/input";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Box from "@cloudscape-design/components/box";
import { makeApiCall } from "../../services/MakeAPICall";

export const ToolSelectionStep = ({ selectedTools, onToolsChange, customTools, onCustomToolsChange }) => {
  const [availableTools, setAvailableTools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTools = async () => {
      try {
        // Try to fetch from API first
        const response = await makeApiCall("GET", "/tools/available");
        const tools = response.tools || [];
        
        // Fallback to hardcoded if API fails or returns empty
        const finalTools = tools.length > 0 ? tools : [
          {name: "DataDog", category: "Monitoring", description: "Logging, auditing, APM"},
          {name: "PreFlight", category: "Security", description: "SAST/DAST scanning"},
          {name: "GitLab", category: "DevOps", description: "Code repository, CI/CD"},
          {name: "Akamai", category: "Security", description: "WAF, OWASP TOP 10, DDoS"},
          {name: "AWS WAF", category: "Security", description: "Web Application Firewall"},
          {name: "AWS CloudFront", category: "CDN", description: "Edge caching, DDoS"},
          {name: "AWS Lambda", category: "Compute", description: "Serverless functions"},
          {name: "AWS API Gateway", category: "API", description: "API management"},
          {name: "AWS Cognito", category: "Auth", description: "Authentication"},
          {name: "AWS Secrets Manager", category: "Security", description: "Credentials"},
        ];
        
        if (tools.length === 0) {
          console.warn("API returned no tools, using hardcoded fallback");
        }
        
        const options = finalTools.map(tool => ({
          label: tool.name,
          value: tool.name,
          description: tool.description,
          tags: [tool.category]
        }));
        
        setAvailableTools(options);
        
        // Select all tools by default
        if (selectedTools.length === 0) {
          onToolsChange(options.map(o => ({ label: o.label, value: o.value })));
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Failed to load tools:", error);
        // Use hardcoded fallback on error
        const fallbackTools = [
          {name: "DataDog", category: "Monitoring", description: "Logging, auditing, APM"},
          {name: "PreFlight", category: "Security", description: "SAST/DAST scanning"},
          {name: "GitLab", category: "DevOps", description: "Code repository, CI/CD"},
          {name: "Akamai", category: "Security", description: "WAF, OWASP TOP 10, DDoS"},
          {name: "AWS WAF", category: "Security", description: "Web Application Firewall"},
          {name: "AWS CloudFront", category: "CDN", description: "Edge caching, DDoS"},
          {name: "AWS Lambda", category: "Compute", description: "Serverless functions"},
          {name: "AWS API Gateway", category: "API", description: "API management"},
          {name: "AWS Cognito", category: "Auth", description: "Authentication"},
          {name: "AWS Secrets Manager", category: "Security", description: "Credentials"},
        ];
        const options = fallbackTools.map(tool => ({
          label: tool.name,
          value: tool.name,
          description: tool.description,
          tags: [tool.category]
        }));
        setAvailableTools(options);
        if (selectedTools.length === 0) {
          onToolsChange(options.map(o => ({ label: o.label, value: o.value })));
        }
        setLoading(false);
      }
    };

    fetchTools();
  }, []);

  return (
    <SpaceBetween size="l">
      <FormField
        label="Approved Tools"
        description="Select which tools should be recommended in mitigations. All TUI-approved tools are selected by default."
      >
        <Multiselect
          selectedOptions={selectedTools}
          onChange={({ detail }) => onToolsChange(detail.selectedOptions)}
          options={availableTools}
          placeholder="Select tools..."
          filteringType="auto"
          loading={loading}
          tokenLimit={3}
        />
      </FormField>

      <FormField
        label="Additional Tools (Optional)"
        description="Add custom tools not in the standard catalog (comma-separated)"
      >
        <Input
          value={customTools}
          onChange={({ detail }) => onCustomToolsChange(detail.value)}
          placeholder="e.g., Custom Security Scanner, Internal Tool"
        />
      </FormField>

      <Box variant="small" color="text-status-info">
        💡 Tip: Only selected tools will be recommended in threat mitigations. 
        Deselect tools your team doesn't have access to.
      </Box>
    </SpaceBetween>
  );
};
